import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map } from 'rxjs';
import { UserService } from '../user/user.service';
import { FILE_BATCH_DOWNLOAD_ENDPOINT, FILE_DOWNLOAD_ENDPOINT, FILE_UPLOAD_ENDPOINT } from 'src/app/constants/api-routes';

/** File upload response type */
export type FileUploadResponse = string;

/** FileEntity response type */
export interface FileEntity {
  id: number;
  fileName: string;
  fileType: string;
  data: Blob;
}

/**
 * FileService handles file uploads and management
 */
@Injectable({
  providedIn: 'root'
})
export class FileService {
  /** Injected dependencies */
  private readonly _http: HttpClient = inject(HttpClient);
  private readonly _userService: UserService = inject(UserService);

  /**
   * Upload an invoice to the backend
   * @param file The file to upload
   * @returns An observable with the uploaded file id
   */
  public uploadInvoice(file: File): Observable<string> {
    // Create formData with file information
    const formData = new FormData();
    formData.append('file', file);

    // Get company id
    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('No company found for user');
    }

    // Perform http request
    return this._http.post<FileUploadResponse>(FILE_UPLOAD_ENDPOINT(companyId), formData);
  }

  /**
   * Download a file from the backend
   * 
   * @param fileId The ID of the file to download
   * @returns An observable with the downloaded file
   */
  public downloadFile(fileId: string): Observable<File> {
    // Get company id
    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('No company found for user');
    }

    // Perform http request with observe: 'response' to get headers
    const url = FILE_DOWNLOAD_ENDPOINT(companyId, fileId);
    return this._http.get(url, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((response: HttpResponse<Blob>) => {
        const blob = response.body;
        if (!blob) {
          throw new Error('No file data received');
        }

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = fileId; // fallback to fileId if no filename found

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        // Get content type from response headers
        const contentType = response.headers.get('Content-Type') || 'application/octet-stream';

        // Create file with proper filename and type
        const file = new File([blob], filename, { type: contentType });
        return file;
      }),
      catchError(error => {
        throw new Error(`Failed to download file: ${error.message}`);
      })
    );
  }

  /**
   * Perform a batch download of multiple files
   * 
   * @param fileIds The list of file IDs to download
   * @return An observable with a list of file ID and File object pairs
   */
  public batchDownloadFiles(fileIds: number[]): Observable<{id: number, file: File}[]> {
    // Get company ID
    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('No company found for user');
    }

    // Perform http requests for all files using batch endpoint
    const url = FILE_BATCH_DOWNLOAD_ENDPOINT(companyId);
    return this._http.post<FileEntity[]>(url, fileIds).pipe(
      map((files: FileEntity[]) => {
        return files.map(f => {
          // Convert base64 data to Blob
          const bytes = Uint8Array.from(atob(f.data as unknown as string), c => c.charCodeAt(0));
          const blob = new Blob([bytes], { type: f.fileType });
          return { id: f.id, file: new File([blob], f.fileName, { type: f.fileType }) };
        });
      })
    );
  }

  /**
   * Open a file in a new tab
   * 
   * @note this method can be changed to open files in a more complicated manner. For now, just open
   * then in a new tab as a blob URL
   * 
   * For filename saving, I think you'd have to add angular routing to set a filename in the URL
   * 
   * @param file The file to open
   */
  public openFile(file: File): void {
    const fileURL = URL.createObjectURL(file);
    const downloadWindow = window.open(fileURL, '_blank');

    // Set the document title to the filename for better UX
    if (downloadWindow) {
      downloadWindow.addEventListener('load', () => {
        downloadWindow.document.title = file.name;
      });
    }
  }
}
